/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { IServerSideGetRowsParams } from 'ag-grid-community';
import { set } from 'lodash';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getOperator } from '@/utils/getOperator';

const NUMERIC_SET_FILTER_FIELDS = new Set(['pregnant', 'exportDonor']);
const ENUM_SET_FILTER_FIELDS = new Set(['breed.subFamily']);

interface IDataSourceProps {
  query: any;
  variables?: any;
  skip?: boolean;
  searchBy: string;
  searchTerm: string;
  ownerId?: string;
}

export function useAdvancedDonorDataSource({
  query,
  variables,
  skip,
  searchBy,
  searchTerm,
  ownerId,
}: IDataSourceProps) {
  const isPregnantExportEnabled = useFeatureFlag(
    'Pregnant_Export_Donor_Displaying_Code',
  );

  const apiRef = useRef<{ api: any } | null>(null);

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      if (
        skip ||
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        success({
          rowData: [],
          rowCount: 0,
        });
        return;
      }

      apiRef.current = { api };

      const order = request.sortModel
        .map((s) => ({
          sort: s.sort,
          field: api.getColumn(s.colId)?.getColDef().field,
        }))
        .filter((s) => s.field)
        .map((s) => set({}, s.field!, s.sort === 'asc' ? 'ASC' : 'DESC'));

      const filterModel = request.filterModel;
      const filterConditions: any[] = [];

      Object.entries(filterModel || {}).forEach(([field, condition]) => {
        const isSetFilter = condition.filterType === 'set';
        const resolveSetValues = () => {
          if (isPregnantExportEnabled && NUMERIC_SET_FILTER_FIELDS.has(field)) {
            return condition.values.map(Number);
          }
          return condition.values;
        };
        const filterValue = isSetFilter
          ? { in: resolveSetValues() }
          : { [getOperator(condition.type)]: condition.filter };

        const [parent, child] = field.split('.');

        if (parent === 'donor') {
          filterConditions.push({
            donor: {
              name: filterValue,
            },
          });
          return;
        }

        if (parent === 'breed') {
          if (isSetFilter && ENUM_SET_FILTER_FIELDS.has(field)) {
            filterConditions.push({
              or: condition.values.map((v: string) => ({
                breed: { [child]: { eq: v } },
              })),
            });
          } else {
            filterConditions.push({
              breed: {
                [child ?? 'code']: filterValue,
              },
            });
          }
          return;
        }

        if (parent === 'owner') {
          filterConditions.push({
            owner: {
              code: filterValue,
            },
          });
          return;
        }

        if (parent === 'yard') {
          filterConditions.push({
            yard: {
              name: filterValue,
            },
          });
          return;
        }

        filterConditions.push(
          child
            ? {
                [parent]: {
                  [child]: filterValue,
                },
              }
            : {
                [field]: filterValue,
              },
        );
      });

      const hasLeadingWildcard = searchTerm.startsWith('*');
      const hasTrailingWildcard = searchTerm.endsWith('*');
      const cleanSearchTerm = searchTerm.replace(/^\*+|\*+$/g, '');

      let searchOperator = 'contains';
      if (hasLeadingWildcard && !hasTrailingWildcard) {
        searchOperator = 'endsWith';
      } else if (hasTrailingWildcard && !hasLeadingWildcard) {
        searchOperator = 'startsWith';
      }

      const actualSearchTerm = cleanSearchTerm || searchTerm;

      const ENUM_FIELDS = new Set(['breed.subFamily']);

      const searchCondition =
        searchBy.length === 0 || ENUM_FIELDS.has(searchBy)
          ? {
              or: [
                { name: { [searchOperator]: actualSearchTerm } },
                { breed: { code: { [searchOperator]: actualSearchTerm } } },
                { owner: { code: { [searchOperator]: actualSearchTerm } } },
                { registrationNumber: { [searchOperator]: actualSearchTerm } },
                { tattoo: { [searchOperator]: actualSearchTerm } },
                { metalTag: { [searchOperator]: actualSearchTerm } },
                { yard: { name: { [searchOperator]: actualSearchTerm } } },
                { ranchTag: { [searchOperator]: actualSearchTerm } },
                { lastActivity: { [searchOperator]: actualSearchTerm } },
              ],
            }
          : searchBy.includes('.')
            ? {
                [searchBy.split('.')[0]]: {
                  [searchBy.split('.')[1]]: {
                    [searchOperator]: actualSearchTerm,
                  },
                },
              }
            : { [searchBy]: { [searchOperator]: actualSearchTerm } };

      const where = {
        and: [
          ...filterConditions,
          searchCondition,
          ownerId ? { ownerId: { eq: ownerId } } : null,
        ].filter(Boolean),
      };

      query({
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order,
        where,
        ...variables,
      })
        .unwrap()
        .then((res: any) => {
          success({
            rowData: res.donors.items,
            rowCount: res.donors.totalCount,
          });
        });
    },
    [
      isPregnantExportEnabled,
      ownerId,
      query,
      searchBy,
      searchTerm,
      skip,
      variables,
    ],
  );

  const refetch = useCallback(() => {
    if (apiRef.current?.api) {
      apiRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [searchBy, searchTerm, ownerId, refetch]);

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch,
    }),
    [getRows, refetch],
  );
}
